import {
  BetaTag,
  Button,
  Heading,
  Label,
  Modal,
  MultiSegmentControl,
  Option,
  toast,
} from "@indigo-components";
import { FC, useCallback, useEffect, useState } from "react";
import {
  FontSize,
  Theme,
  useFontSize,
  useTheme,
} from "~/hooks/useDesignSystemToggle";
import { useUserSettings } from "~/hooks/useUserSettings";
import { AuthUserGroup } from "~/types/auth";

interface SettingsOption<T> {
  label: string; // Professional label (for DOCTOR)
  altLabel: string; // Fun label (for ADMIN/MEDICAL_ASSISTANT)
  value: T; // Actual value (FontSize | Theme)
  hidden: boolean; // Hide experimental options
}

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userGroup: AuthUserGroup;
}

// Font size options with dual labels
const fontSizeOptions: SettingsOption<FontSize>[] = [
  { label: "Smaller", altLabel: "Eric", value: "smaller", hidden: false },
  { label: "Default", altLabel: "Default", value: "default", hidden: false },
  { label: "Larger", altLabel: "Helen", value: "larger", hidden: false },
];

// Theme options with dual labels
const themeOptions: SettingsOption<Theme>[] = [
  { label: "Light", altLabel: "Cloud", value: "light", hidden: false },
  { label: "Warm", altLabel: "Latte", value: "latte", hidden: false },
  {
    label: "Banana",
    altLabel: "Banana",
    value: "overripe-banana",
    hidden: true, // Hidden from Doctor roles (experimental)
  },
  { label: "Dark", altLabel: "Licorice", value: "dark", hidden: false },
];

// Ops checklist default. Unlike the two above this one is not a rendering
// preference, so it persists on the user record instead of localStorage.
// MultiSegmentControl is string-keyed, so the boolean the field stores is
// mapped at this boundary rather than threaded through the control.
type OpsChecklistDefault = "expanded" | "collapsed";

const opsChecklistOptions: Option<OpsChecklistDefault>[] = [
  { label: "Expanded", value: "expanded" },
  { label: "Collapsed", value: "collapsed" },
];

// Groups the Ops checklist sidebar auto-opens for — mirrors MA_SCOPED_GROUPS in
// AppointmentDetailsPage, which is what decides whether the auto-open runs at
// all. Doctors and historians never see the sidebar, so the setting is hidden
// from them rather than offered as a no-op.
const OPS_CHECKLIST_GROUPS: ReadonlyArray<AuthUserGroup> = [
  AuthUserGroup.ADMIN,
  AuthUserGroup.MEDICAL_ASSISTANT,
];

/**
 * Convert SettingsOption array to Option array based on user group
 * - DOCTOR users see professional labels and hidden options are filtered OUT
 * - ADMIN/MEDICAL_ASSISTANT users see fun labels and all options (including experimental/hidden)
 */
const getDisplayOptions = <T,>(
  options: SettingsOption<T>[],
  userGroup: AuthUserGroup,
): Option<T>[] => {
  const isDoctor = userGroup === AuthUserGroup.DOCTOR;

  return options
    .filter((opt) => {
      // For doctors: exclude hidden options
      // For ADMIN/MA: include all options (even hidden ones)
      return !isDoctor || !opt.hidden;
    })
    .map((opt) => ({
      label: isDoctor ? opt.label : opt.altLabel,
      value: opt.value,
    }));
};

const UserSettingsModal: FC<UserSettingsModalProps> = ({
  isOpen,
  onClose,
  userGroup,
}) => {
  // Get current theme and font size from global state
  const [currentTheme, setCurrentTheme] = useTheme();
  const [currentFontSize, setCurrentFontSize] = useFontSize();
  const { settings, updateSettings, isSaving } = useUserSettings();

  // Server-backed, so it is not previewed live like theme/font size: the write
  // only goes out on Done. Null (never set) reads as collapsed.
  const savedOpsChecklist: OpsChecklistDefault =
    settings.opsChecklistDefaultExpanded ? "expanded" : "collapsed";
  const [localOpsChecklist, setLocalOpsChecklist] =
    useState<OpsChecklistDefault>(savedOpsChecklist);

  // Local state for editing (allows cancel/revert)
  const [localTheme, setLocalTheme] = useState<Theme>(currentTheme);
  const [localFontSize, setLocalFontSize] = useState<FontSize>(currentFontSize);

  // Store original values when modal opens
  const [originalTheme, setOriginalTheme] = useState<Theme>(currentTheme);
  const [originalFontSize, setOriginalFontSize] =
    useState<FontSize>(currentFontSize);

  // Sync local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalTheme(currentTheme);
      setLocalFontSize(currentFontSize);
      setOriginalTheme(currentTheme);
      setOriginalFontSize(currentFontSize);
      setLocalOpsChecklist(savedOpsChecklist);
    }
  }, [isOpen, currentTheme, currentFontSize, savedOpsChecklist]);

  // Apply local changes immediately for preview
  useEffect(() => {
    if (isOpen) {
      setCurrentTheme(localTheme);
      setCurrentFontSize(localFontSize);
    }
  }, [localTheme, localFontSize, isOpen, setCurrentTheme, setCurrentFontSize]);

  // Get display options based on user group
  const displayFontSizeOptions = getDisplayOptions(fontSizeOptions, userGroup);
  const displayThemeOptions = getDisplayOptions(themeOptions, userGroup);
  const showOpsChecklistSetting = OPS_CHECKLIST_GROUPS.includes(userGroup);

  const handleCancel = useCallback(() => {
    // Revert to original values
    setCurrentTheme(originalTheme);
    setCurrentFontSize(originalFontSize);
    setLocalTheme(originalTheme);
    setLocalFontSize(originalFontSize);
    onClose();
  }, [
    originalTheme,
    originalFontSize,
    onClose,
    setCurrentTheme,
    setCurrentFontSize,
  ]);

  const handleConfirm = useCallback(async () => {
    // Theme and font size are already applied; only the server-backed setting
    // needs a write, and only when it actually changed.
    if (showOpsChecklistSetting && localOpsChecklist !== savedOpsChecklist) {
      const saved = await updateSettings({
        opsChecklistDefaultExpanded: localOpsChecklist === "expanded",
      });

      // Closing on a failed write would leave the user believing the default
      // changed, and they'd only find out on the next case. Say so and keep
      // the modal open so Done can be retried.
      if (!saved) {
        toast({
          message: "Could not save your Ops checklist default. Please retry.",
          variant: "error",
        });
        return;
      }
    }
    onClose();
  }, [
    showOpsChecklistSetting,
    localOpsChecklist,
    savedOpsChecklist,
    updateSettings,
    onClose,
  ]);

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      centered
      width={520}
      className="flex flex-col justify-between p-6"
      title={
        <div className="flex items-center gap-2">
          <Heading level={1}>User Settings</Heading>
          <BetaTag size="lg" />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Font Size Control */}
        <div className="flex justify-between items-center ">
          <Label size="lg" weight="medium">
            Font size
          </Label>
          <MultiSegmentControl
            options={displayFontSizeOptions}
            value={localFontSize}
            onChange={(value: FontSize) => setLocalFontSize(value)}
            variant="filled"
          />
        </div>

        {/* Theme Color Control */}
        <div className="flex justify-between items-center">
          <Label size="lg" weight="medium">
            Theme color
          </Label>
          <MultiSegmentControl
            options={displayThemeOptions}
            value={localTheme}
            onChange={(value: Theme) => setLocalTheme(value)}
            variant="filled"
          />
        </div>

        {/* Ops Checklist Default */}
        {showOpsChecklistSetting && (
          <div className="flex justify-between items-center">
            <Label size="lg" weight="medium">
              Ops checklist default
            </Label>
            <MultiSegmentControl
              options={opsChecklistOptions}
              value={localOpsChecklist}
              onChange={(value: OpsChecklistDefault) =>
                setLocalOpsChecklist(value)
              }
              variant="filled"
            />
          </div>
        )}

        {/* Footer Buttons */}
        <div className="flex justify-end gap-3 mt-2">
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={handleConfirm}
            disabled={isSaving}
          >
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UserSettingsModal;
